import {
  createClient,
  LiveClient,
  LiveTranscriptionEvents,
  LiveTranscriptionEvent
} from "@deepgram/sdk";
import { apiRequest } from "../api";

// Define the expected event types to match Deepgram SDK
type TranscriptEventType = "Results" | "Metadata" | "UtteranceEnd" | "Error";

export class DeepGramStt {
  private socket: LiveClient | null = null;
  private transcript: string = "";
  private requestId: string = "";
  private onTranscribe: (transcript: string) => void;

  // Add getter for socket
  getSocket(): LiveClient | null {
    return this.socket;
  }

  init = async (
    onTranscribe: (transcript: string) => void,
    keyterm: string[],
    trackEvent: (title: string, payload?: {}) => void,
    trackError: (title: string, error, payload?: {}) => void
  ): Promise<void> => {
    this.requestId = "";
    this.transcript = "";
    this.onTranscribe = onTranscribe;

    const data = await apiRequest<{ token: string }>({
      subRoute: "ai",
      path: "/get_deepgram_token",
      method: "GET"
    });

    const client = createClient(data.token);

    this.socket = client.listen.live({
      smart_format: true,
      model: "nova-3-general",
      language: "en-US",
      interim_results: true,
      punctuate: true,
      keyterm: Array.from(new Set(keyterm))
    });

    await new Promise<boolean>((resolve) => {
      if (!this.socket) return;

      this.socket.addListener("open", async () => {
        const readyInterval = setInterval(() => {
          if (this.socket?.getReadyState() === 1) {
            clearInterval(readyInterval);
            resolve(true);
          }
        }, 500);

        this.socket.addListener("close", async () => {
          trackEvent("deepgram_closing");
          clearInterval(readyInterval);
          this.socket?.removeAllListeners();
        });

        this.socket.addListener("error", async (error) => {
          clearInterval(readyInterval);
          trackError("deepgram_error", error);
          this.end();
        });

        this.socket.addListener(
          LiveTranscriptionEvents.Transcript,
          (data: LiveTranscriptionEvent) => {
            // Cast type to our union type to ensure compatibility
            const type = data.type as TranscriptEventType;

            switch (type) {
              case "Results":
                if (data.is_final === true) {
                  const liveTranscript =
                    data?.channel?.alternatives?.[0]?.transcript || "";

                  if (liveTranscript && liveTranscript.length > 0) {
                    if (
                      this.transcript.length > 0 &&
                      this.transcript[this.transcript.length - 1] !== "\n"
                    ) {
                      this.transcript += " ";
                    }

                    this.transcript += liveTranscript;
                    this.onTranscribe(this.transcript);

                    if (!this.requestId) {
                      this.requestId = data.metadata.request_id;
                    }
                  }
                }
                break;
              case "Metadata":
                break;
              default:
                trackError("deepgram_unknown_packet", null, { data });
                break;
            }
          }
        );
      });
    });
  };

  end = (): { transcript: string; requestId: string } => {
    let transcript = this.transcript;

    try {
      this.socket?.requestClose();
      this.socket?.removeAllListeners();
    } catch (_) {}

    this.socket = null;

    transcript = transcript.trim();
    if (
      transcript.length > 0 &&
      !["?", "!", "."].includes(transcript[transcript.length - 1])
    ) {
      transcript += ".";
    }

    if (this.onTranscribe) {
      this.onTranscribe(transcript);
    }

    this.transcript = "";

    return {
      transcript,
      requestId: this.requestId
    };
  };
}
